import React, { useState } from 'react';
import GeneralCapitalCallCard from './GeneralCapitalCallCard';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import PartnerTransferActionBar from '../../PartnerTransferActionBar';
import { useLayoutContext } from 'contexts/LayoutContext';
import api from 'lib/trpcClient';
import { useFormContext } from 'react-hook-form';
import { PartnerTransfersFormValues } from '../../hooks/usePartnerTransfersForm';
import { PartnerTransferSection } from 'further-types/lp/partner-transfer';
import { AllocationRule } from 'further-types/lp/allocations';
import Page from 'components/new/Page';
import { PARTNER_TRANSFERS_LIST } from 'adminConstants/routes';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import { useExpandedRows } from 'hooks/ui/useExpandedRows';
import ColorIndicatorWithText from 'components/new/ColorIndicatorWithText';

interface GeneralCapitalAction {
  actionId: string;
  entryId: string;
  name: string;
  ledgerDate: CalendarDay;
  actionDueDate?: CalendarDay;
  totalAmountLcy: number;
  allocationRule: AllocationRule;
  originalInvestorBreakdown: Array<{
    commitmentId: string;
    investorName: string;
    totalCommitmentLcy: number;
    calledFundsLcy: number;
    allocationPercentage: number;
  }>;
  newInvestorBreakdown: Array<{
    commitmentId: string;
    investorName: string;
    totalCommitmentLcy: number;
    calledFundsLcy: number;
    allocationPercentage: number;
    excludeFromAllocation: boolean;
    callOverpaidOrUnderpaid: number;
  }>;
  numberOfEntries: number;
}

interface GeneralCapitalCallsContentProps {
  partnerTransferId: string;
  generalCapitalActions: Array<GeneralCapitalAction>;
  baseCurrency: CurrencyCode;
  onNext: () => void;
  onPrevious: () => void;
  stepper: React.ReactNode;
}

const GeneralCapitalCallsContent: React.FC<GeneralCapitalCallsContentProps> = ({
  partnerTransferId,
  generalCapitalActions,
  baseCurrency,
  onNext,
  onPrevious,
  stepper,
}) => {
  const { selectedVehicleId } = useLayoutContext();
  const { getValues } = useFormContext<PartnerTransfersFormValues>();
  const { expandedRows, toggleRowExpansion } = useExpandedRows();

  const [isNavigating, setIsNavigating] = useState(false);
  const updatePartnerTransfer = api.partnerTransfers.update.useMutation();

  const rows = generalCapitalActions.map((action) => ({
    ...action,
    id: action.actionId,
  }));

  const columns: Array<TableColumn<(typeof rows)[number]>> = [
    {
      key: 'name',
      label: 'Name',
      render: ({ name }) => (
        <ColorIndicatorWithText text={name} colorKey={{ name }} />
      ),
    },
    {
      key: 'ledgerDate',
      label: 'Date',
      render: (row) => row.ledgerDate.toReadable(),
    },
    {
      key: 'totalAmountLcy',
      label: 'Amount',
      render: (row) =>
        numberToCurrencyString(row.totalAmountLcy, {
          fallback: '-',
          currency: baseCurrency,
        }),
    },
  ];

  const handleNext = () => {
    setIsNavigating(true);
    const formValues = getValues();
    const input = {
      partnerTransferId,
      vehicleId: selectedVehicleId ?? '',
      transferDate: formValues.transferDate,
      transferorCommitmentId: formValues.trasferrorCommitmentId,
      currentSection: PartnerTransferSection.GeneralCapitalActions,
      transferees: formValues.transferees.map((transferee) => ({
        investorId: transferee.investorId,
        amountLcy: transferee.amountLcy,
        percentage: transferee.percentage,
      })),
      saveActionGeneralCapitalActions: true,
    };
    updatePartnerTransfer.mutate(input, {
      onSuccess: () => onNext(),
      onError: () => setIsNavigating(false),
    });
  };

  const handlePrevious = () => {
    onPrevious();
  };

  return (
    <Page
      goBackAction={PARTNER_TRANSFERS_LIST}
      spacing="lg"
      heading="Partner transfer"
      withHeadingDivider
      customActions={stepper}
      actionBar={
        <PartnerTransferActionBar
          onPrevious={handlePrevious}
          onNext={handleNext}
          isNextLoading={isNavigating}
          isNextDisabled={isNavigating}
          isPreviousDisabled={isNavigating}
        />
      }
    >
      <Table
        isExpandableRow={() => true}
        isExpandedRow={(row) => expandedRows.has(row.id)}
        onRowClick={(row) => toggleRowExpansion(row.id)}
        onRowExpand={(row) => toggleRowExpansion(row.id)}
        getExpandedContent={(row) => (
          <GeneralCapitalCallCard
            generalCapitalAction={row}
            baseCurrency={baseCurrency}
          />
        )}
        columns={columns}
        rows={rows}
        emptyMessage="No general capital actions found for this partner transfer."
      />
    </Page>
  );
};

export default GeneralCapitalCallsContent;
